import { useQuery } from "@tanstack/react-query";
import { AuthService } from "./auth";
import type { UserProfile } from "../types";

export interface ProfileImageData {
  url: string | null;
}
export const profileImageKey = (email?: string) =>
  ["profile-image", email ?? null] as const;

export async function profileImageRequest(
  method: "GET" | "PUT" | "DELETE",
  file?: File,
  signal?: AbortSignal,
): Promise<ProfileImageData> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(abort, 15000);
  try {
    const response = await fetch("/api/auth/profile-image", {
      method,
      credentials: "include",
      signal: controller.signal,
      body: file,
      headers:
        method === "GET"
          ? undefined
          : {
              "X-Profile-Image-Request": "1",
              ...(file ? { "Content-Type": file.type } : {}),
            },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok)
      throw new Error(data.message || "画像の処理に失敗しました。");
    return data;
  } catch (error) {
    if (controller.signal.aborted)
      throw new Error("通信が完了しませんでした。再試行してください。");
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export function useProfileImage() {
  const { data: user } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => AuthService.getInstance().getCurrentUser(),
    staleTime: 300_000,
  });
  const query = useQuery({
    queryKey: profileImageKey(user?.email),
    queryFn: ({ signal }) => profileImageRequest("GET", undefined, signal),
    retry: false,
    enabled: !!user?.email,
    staleTime: 30_000,
  });
  return { ...query, email: user?.email };
}
